import { isRegisterable } from '../utilities/guards';
/** The name of the module */
export const name = 'ComponentRegistry';
/**
 * Type guard for determining if a value is a component key
 *
 * @param str Value to be tested
 */
const isComponentKey = (str) => typeof str === 'string';
class ComponentRegistry {
    /**
       * Add a component to the registry
       *
       * @param key HTML element the registry is for
       * @param componentKey Name of the component being registered
       * @param component Component instance being registered
       */
    static add(key, componentKey, component) {
        ComponentRegistry.addComponent(key, componentKey, component);
    }
    /**
       * Remove a component from the registry
       *
       * @param key HTML element to be removed
       * @param componentOrComponentKey Name of the component or component instance to remove
       */
    static remove(key, componentOrComponentKey) {
        // Is a component/component instance included?
        if (typeof componentOrComponentKey === 'undefined') {
            // If not, then remove all entries for the provided HTML element
            ComponentRegistry.removeComponent(key);
        }
        else {
            // If so, then fetch the components
            const components = ComponentRegistry.getComponents(key);
            let removalKey;
            // Is a component or component instance specified?
            if (isRegisterable(componentOrComponentKey)) {
                // const filteredComponents = components.filter(component => component !== componentOrComponentKey);
                removalKey = Object.keys(components).filter((componentKey) => components[componentKey] === componentOrComponentKey);
            }
            else if (typeof componentOrComponentKey === 'string') {
                removalKey = componentOrComponentKey;
            }
            // Delete the entry by component name from the registry
            delete components[removalKey];
            ComponentRegistry.setComponents(key, components);
        }
    }
    /**
       * Get all components or a specific component for an HTML element
       *
       * @param key HTML element to fetch components for
       * @param componentKey Name of the component to fetch
       */
    static get(key, componentKey) {
        // If a component key is provided, then return the specific key
        if (isComponentKey(componentKey)) {
            return ComponentRegistry.getComponent(key, componentKey);
        }
        // Otherwise return all components for the HTML element
        return ComponentRegistry.getComponents(key);
    }
    /**
       * Remove all components for a provided HTML element
       *
       * @param key HTML element to remove from the registry
       */
    static removeComponent(key) {
        ComponentRegistry.registry.delete(key);
    }
    /**
       * Get all components registered to an HTML element
       *
       * @param key HTML element to get components for
       */
    static getComponents(key) {
        return ComponentRegistry.registry.get(key);
    }
    /**
       * Get a specific component for an HTML element
       *
       * @param key HTML element to get component for
       * @param componentKey Name of the component to fetch
       */
    static getComponent(key, componentKey) {
        return ComponentRegistry.registry.get(key)[componentKey];
    }
    /**
       * Register components for an HTML element
       *
       * @param key HTML element to set components for
       * @param components Components object to register
       */
    static setComponents(key, components) {
        ComponentRegistry.registry.set(key, components);
    }
    /**
       * Register a particular component for an HTML element
       *
       * @param key HTML element to register
       * @param componentKey Name of the component to register
       * @param component Component instance to register
       */
    static addComponent(key, componentKey, component) {
        // Get all components, if any, for the provided HTML element
        const components = ComponentRegistry.getComponents(key);
        // Were any components found?
        if (components) {
            // If yes, then update the components object for the HTML element
            ComponentRegistry.setComponents(key, {
                ...components,
                [componentKey]: component,
            });
        }
        else {
            // if not, then create and add a new component object
            ComponentRegistry.setComponents(key, {
                [componentKey]: component,
            });
        }
    }
}
/** The registry */
ComponentRegistry.registry = new Map();
export default ComponentRegistry;
